import { useCallback, useMemo, useRef, useState } from 'react';

import { RotateCcwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DateRangeMenu } from '@/modules/analytics/components/filters/DateFilter/DateRangeMenu';
import {
  type DateRange,
  type DateSelection,
  periodToSelection,
  resolveDateSelection,
  selectionToPeriod,
} from '@/modules/analytics/dateSelection';
import { type Period } from '@/modules/analytics/types';
import { formatDateRangeLabel } from '@/modules/analytics/utils/formatDateRangeLabel';

interface LocalDateFilterProps {
  selection: DateSelection;
  onSelectionChange: (selection: DateSelection) => void;
  align?: 'start' | 'end';
  className?: string;
  /** Label shown when no range is set. Also enables the clear button beside the trigger. */
  placeholder?: string;
}

/**
 * A lightweight date filter component with local state management.
 * Unlike the global DateFilter, this doesn't use Redux state.
 */
export const LocalDateFilter = ({
  selection,
  onSelectionChange,
  align = 'end',
  className,
  placeholder,
}: LocalDateFilterProps) => {
  const { t } = useTranslation();
  // Always derive concrete dates from the current selection (resolved against today).
  const dateRange = useMemo(() => resolveDateSelection(selection), [selection]);
  const [dateOpen, setDateOpen] = useState(false);

  const menuRef = useRef(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  useClickAway(menuRef, (event) => {
    if (calendarRef.current?.contains(event.target as Node)) {
      return;
    }
    setDateOpen(false);
  });

  // From the stored selection, not by matching dates: two presets can produce the same range.
  const selectedPeriod = useMemo(() => selectionToPeriod(selection) ?? null, [selection]);

  const handlePresetSelect = useCallback(
    (period: Period) => {
      const next = periodToSelection(period);
      if (next) {
        onSelectionChange(next);
      }
      setDateOpen(false);
    },
    [onSelectionChange]
  );

  const handleRangeSelect = useCallback(
    (range: DateRange) => {
      onSelectionChange({ type: 'custom', ...range });
      setDateOpen(false);
    },
    [onSelectionChange]
  );

  // No retention gating: unlike the analytics and activity-log filters, these pages are not bound by it.
  const today = useMemo(() => new Date(new Date().setHours(0, 0, 0, 0)), []);
  const isDayDisabled = useCallback((day: Date) => day > today, [today]);

  const getDateButtonLabel = () => {
    const { startDate, endDate } = dateRange;
    if (startDate && endDate) {
      return formatDateRangeLabel(startDate, endDate);
    }
    const single = startDate || endDate;
    if (single) {
      return formatDateRangeLabel(single, single);
    }
    return placeholder || t('analytics.periods.custom');
  };

  const handleClearClick = useCallback(() => {
    onSelectionChange({ type: 'none' });
    setDateOpen(false);
  }, [onSelectionChange]);

  const hasActiveRange = !!dateRange.startDate;

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div className="relative z-30" ref={menuRef}>
        <Button
          variant="outline-neutral"
          size="sm"
          className="leading-6"
          aria-expanded={dateOpen}
          aria-haspopup="dialog"
          onClick={() => setDateOpen((prev) => !prev)}
        >
          {getDateButtonLabel()}
        </Button>

        {dateOpen ? (
          <DateRangeMenu
            ref={calendarRef}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onPresetSelect={handlePresetSelect}
            onRangeSelect={handleRangeSelect}
            isDayDisabled={isDayDisabled}
            today={today}
            selectedPeriod={selectedPeriod}
            align={align}
            onRequestClose={() => setDateOpen(false)}
          />
        ) : null}
      </div>

      {placeholder && hasActiveRange ? (
        <InfoTooltip content={t('analytics.clear_date_filter')} side="right">
          <button
            type="button"
            aria-label={t('analytics.clear_date_filter')}
            className="inline-flex size-9 items-center justify-center gap-0 rounded-lg p-2.5 text-muted-foreground hover:bg-selected-soft"
            onClick={handleClearClick}
          >
            <RotateCcwIcon size={16} />
          </button>
        </InfoTooltip>
      ) : null}
    </div>
  );
};
